/**
 * Defines the core logic for creating and rendering type-safe, dynamic Firestore paths.
 * This module is consumed by `ModelSpec` to define collection paths.
 * @see {@link https://www.typescriptlang.org/docs/handbook/2/template-literal-types.html} - The system is heavily inspired by and relies on TypeScript's template literal types.
 * @see {@link https://zod.dev/?id=brand} - The concept of `Resolvable` segments is built upon Zod's `brand` feature to create distinct string types.
 *
 * This file provides the foundational pathing mechanism for the entire `@firebolt` data layer.
 * The `TemplatePath` class allows for the creation of path templates using tagged template literals (e.g., `collectionPath`chats/${chatId}`).
 *
 * The key innovation here is the use of Zod-branded strings (`pathId`) as "Resolvable" placeholders. This provides compile-time safety for path parameters.
 * The `render` and `toPath` methods resolve these placeholders with actual values at runtime, constructing a valid Firestore path string.
 * This system ensures that it's impossible to construct a path with missing or incorrectly named parameters, preventing a common class of runtime errors.
 */

import { z } from 'zod';

import { assert } from '@elara/core/errors';

/**
 * An interface for objects that can be converted into a string segment for a Firestore path.
 */
export interface HasPathSegment {
  toPathSegment(): string;
}

/**
 * Represents a single segment of a dynamic path. It can be a static string,
 * an object that can be converted to a string, or a `Resolvable` placeholder.
 */
export type PathSegment = string | HasPathSegment | Resolvable;

/**
 * Defines the signature for a function that builds a `TemplatePath` from a template literal.
 */
export type TemplatePathBuilder<SegmentType extends PathSegment> = <Segment extends SegmentType[]>(
  staticSegments: TemplateStringsArray,
  ...seg: Segment
) => TemplatePath<Segment>;

/**
 * Infers the shape of the object required to render a `TemplatePath`.
 * For a path like `chats/${chatId}/messages/${messageId}`, it will infer the type `{ chatId: string, messageId: string }`.
 */
export type PathInput<T extends PathSegment[]> = { [K in ResolvableKey<T[number]>]?: string | HasPathSegment };

/**
 * A Zod-branded type that acts as a named placeholder in a `TemplatePath`.
 * This is the core of the type-safe pathing system.
 * @template Name - The unique name of the placeholder (e.g., "chatId").
 * @template T - The underlying Zod type (usually `z.ZodString`).
 */
export type Resolvable<Name extends string = string, T extends z.ZodTypeAny = z.ZodString> = T & {
  readonly __resolvableKey?: Name;
};

/**
 * A utility type to extract the string name from a `Resolvable` type.
 */
export type ResolvableKey<T extends PathSegment> = T extends Resolvable<infer K extends string, any> ? K : never;

/**
 * A utility type to extract the underlying Zod type from a `Resolvable` type.
 */
export type ResolvableValue<T extends PathSegment> = T extends Resolvable<any, infer V> ? V : never;

/**
 * @internal
 * A global map that stores metadata for each `Resolvable` type, primarily its key name.
 * This allows runtime logic to inspect the branded type and retrieve its name.
 */
export const resolvableState = new Map<object, { key: string }>();

/**
 * @internal
 * A factory function to create `Resolvable` types. It takes a base Zod type
 * and returns a function that can brand it with a given key.
 * @param type The base Zod type (e.g., `z.string()`).
 * @returns A function that creates a new `Resolvable` type.
 */
function buildResolvable<T extends z.ZodTypeAny>(type: T) {
  return <Name extends string>(key: Name): Resolvable<Name, T> => {
    const resolvable = type.clone().brand(key) as Resolvable<Name, T>;
    resolvableState.set(resolvable, { key });
    Object.defineProperties(resolvable, {
      ['toPathSegment']: { value: () => key, writable: false, enumerable: false, configurable: true },
      [Symbol.toStringTag]: { value: () => `[${key}]`, configurable: true },
    });
    return resolvable;
  };
}

/**
 * A specific type of `Resolvable` representing a document ID. It's a Zod string branded with a name.
 * @template Name - The name of the ID field (e.g., "userId", "chatId").
 */
export type ZodDocId<Name extends string = string> = Resolvable<Name>;

/**
 * The primary factory function for creating document ID placeholders for use in `TemplatePath`.
 * @example const userId = pathId("userId");
 */
export const pathId: <Name extends string>(key: Name) => ZodDocId<Name> = buildResolvable(z.string());

/**
 * @internal
 * Type guard to check if a value is a `Resolvable` placeholder.
 */
function isResolvable(value: unknown): value is Resolvable {
  return value !== null && typeof value === 'object' && resolvableState.has(value);
}

/**
 * @internal
 * Retrieves the metadata (specifically the key) for a `Resolvable` type.
 */
function getResolvableState(value: Resolvable): { key: string } {
  return resolvableState.get(value)!;
}

/**
 * A utility type that is an alias for `PathInput`. Represents the context object needed to render a path.
 * @deprecated Use `PathInput` directly for clarity.
 */
export type PathContext<P extends TemplatePath> =
  P extends TemplatePath<infer Segments> ? { [K in ResolvableKey<Segments[number]>]: string | HasPathSegment } : never;

/**
 * A utility type for extending a `TemplatePath` with additional properties or methods.
 */
export type ExtendedPath<Segments extends PathSegment[], Extension> = TemplatePath<Segments> & Extension;

/**
 * @class TemplatePath
 * @classdesc Represents a type-safe, dynamic path constructed from a template literal.
 * It separates the static parts of a path from the dynamic, resolvable segments.
 * @template Segments - An array of `PathSegment` types that make up the dynamic parts of the path.
 */
export class TemplatePath<Segments extends PathSegment[] = PathSegment[]> {
  /**
   * Creates a path builder function (a "tagged template" function) that can be used to generate type-safe paths.
   * This allows for the creation of domain-specific path builders.
   *
   * @param [validate] - An optional function to validate the structure of the path segments.
   * @param [extend] - An optional function to add custom methods or properties to the created `TemplatePath` instance.
   * @returns A function that can be used as a tagged template literal.
   */
  static createBuilder<SegmentType extends PathSegment, Extension>(
    validate?: (segments: SegmentType[], staticSegments: string[], _?: SegmentType[]) => boolean,
    extend?: <Segments extends SegmentType[]>(path: TemplatePath<Segments>) => Extension,
  ) {
    return <Segments extends SegmentType[]>(
      templateArray: TemplateStringsArray,
      ...segments: Segments
    ): ExtendedPath<Segments, Extension> => {
      const staticSegments = [...templateArray];
      if (staticSegments.at(-1) === '') {
        staticSegments.pop();
      }
      if (validate) {
        assert(
          validate(segments, staticSegments),
          `Invalid path segments: expected ${segments.length} segments but got ${staticSegments.length - 1} in path template`,
        );
      }
      const path = new this<Segments>(staticSegments, segments);
      return Object.assign(path, extend ? extend(path) : {}) as ExtendedPath<Segments, Extension>;
    };
  }

  /**
   * @constructor
   * @param staticSegments - The static string parts of the path template.
   * @param segments - The dynamic `Resolvable` parts of the path template.
   */
  constructor(
    readonly staticSegments: string[],
    readonly segments: Segments,
  ) {
    assert(
      (this.segments.length + this.staticSegments.length) % 2 === 0,
      `TemplatePath must have an even number of segments. Found ${this.segments.length} segments.`,
    );
  }

  /**
   * @get pairs
   * A computed property that returns the static and dynamic segments as an array of pairs.
   * @returns
   */
  get pairs(): Array<[string, Segments[number]]> {
    return this.segments.map((segment, i) => [this.staticSegments[i]!, segment]);
  }

  /**
   * @get lastPair
   * A computed property that returns the last pair of static and dynamic segments.
   * Useful for determining the final collection name in a path.
   * @returns
   */
  get lastPair(): [string, Segments[number]] {
    const last = this.pairs.at(-1);
    assert(last, 'TemplatePath must have at least one segment.');
    return last;
  }

  /**
   * @internal
   * A generic compiler method to process the path segments.
   */
  compile<T>(compile: (pfx: string, seg: Segments[number], res: T | undefined) => T): T {
    let compiled: T | undefined;
    for (let i = 0; i < this.staticSegments.length; i++) {
      const prefix = this.staticSegments[i]!;
      const segment = this.segments[i]!;
      assert(segment !== undefined, 'TemplatePath must have an even number of segments.');
      compiled = compile(prefix, segment, compiled);
    }
    return compiled!;
  }

  /**
   * Renders the path template into a final string using a resolver function.
   * @param resolve - A function that takes a dynamic segment and returns its string value.
   * @param [options] - Rendering options.
   * @param [options.throwOnMissing=false] - If true, throws an error if a value for a dynamic segment is not provided.
   * @returns The final rendered path string.
   */
  render(
    resolve: (segment: Segments[number]) => string | HasPathSegment | undefined,
    { throwOnMissing = false } = {},
  ): string {
    const rendered = this.compile<string>((prefix, segment, compiled = '') => {
      if (segment === undefined) {
        return `${compiled}${prefix}`;
      }
      let value: string | HasPathSegment | undefined;
      if (isResolvable(segment)) {
        const key = getResolvableState(segment).key;
        value = resolve(key);
      } else {
        value = segment;
      }
      if (value === undefined || value === null) {
        if (throwOnMissing) {
          throw new Error(`Value for path parameter '${this.renderSegment(segment)}' cannot be undefined or null.`);
        } else {
          compiled += prefix;
          if (compiled.at(-1) === '/') {
            // if the last character is a slash, drop it, we're operating on a collection
            const collectionPath = compiled.slice(0, -1);
            // console.debug(`Rendering collection path: ${collectionPath}`);
            return collectionPath;
          } else {
            throw new Error(
              `Value for path parameter '${this.renderSegment(segment)}' in path template '${this.toString()}' cannot be undefined or null.`,
            );
          }
        }
      }
      if (hasPathSegment(value)) {
        value = value.toPathSegment();
      }
      return `${compiled}${prefix}${value}`;
    });

    return rendered;
  }

  /**
   * Renders the path using a context object of substitutions.
   * The object's keys should match the names of the dynamic `Resolvable` segments.
   *
   * @param substitutions - An object mapping segment names to their values.
   * @returns The final rendered path string.
   */
  toPath(substitutions: PathInput<Segments>): string {
    return this.render((segment) => substitutions[segment]);
  }

  /**
   * Renders the collection path by omitting the final document ID placeholder.
   * This is used when you need a reference to a collection rather than a specific document.
   *
   * For example:
   * - `chats/${id}` with {} returns `chats`
   * - `chats/${chatId}/messages/${id}` with {chatId: 'abc'} returns `chats/abc/messages`
   *
   * @param substitutions - Context without the document ID
   * @returns The collection path string
   */
  toCollectionPath(substitutions: Omit<PathInput<Segments>, 'id'>): string {
    // Render with all provided substitutions, but explicitly set 'id' to undefined
    // This will cause the render method to drop the trailing segment
    return this.render((segment) => {
      if (segment === 'id') return;
      return substitutions[segment as keyof typeof substitutions];
    });
  }

  /**
   * Renders the full document path, throwing an error if any required parameter is missing.
   * This ensures that all path parameters are provided when creating a document reference.
   *
   * @param substitutions - An object mapping all segment names to their values
   * @returns The complete document path string
   * @throws {Error} If any required path parameter is missing
   */
  toDocumentPath(substitutions: PathInput<Segments>): string {
    return this.render((segment) => substitutions[segment], { throwOnMissing: true });
  }

  /**
   * Returns a string representation of the path template, showing the placeholders.
   * @returns
   */
  toString() {
    return this.render((segment) => `[${segment}]`, { throwOnMissing: false });
  }

  /**
   * @protected
   * Renders a single segment for debugging or error messages.
   */
  protected renderSegment(segment: PathSegment): string {
    if (isResolvable(segment)) {
      return getResolvableState(segment).key;
    } else if (hasPathSegment(segment)) {
      return (segment as HasPathSegment).toPathSegment();
    } else {
      return segment;
    }
  }
}

/**
 * @internal
 * Type guard to check if a value is a `HasPathSegment` object.
 */
const hasPathSegment = (value: unknown): value is HasPathSegment =>
  typeof value === 'object' && value !== null && 'toPathSegment' in value;

/**
 * @deprecated This function is not used and `TemplatePath.createBuilder` is preferred for creating path builders.
 */
export function dynamicPath<Segments extends PathSegment[]>(
  staticSegments: TemplateStringsArray,
  ...seg: Segments
): TemplatePath<Segments> {
  return new TemplatePath([...staticSegments], seg);
}
